import React, { useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { generateHref } from 'Helmet/GoustoHelmet'
import Helmet from 'react-helmet'
import { useDispatch } from 'react-redux'
import useSWR from 'swr'

import { CWCHomepage } from '@features/cwc-homepage'
import { Homepage } from '@features/homepage'
import type { Recipe } from '@library/menu-service'
import { fromAPIRecipe, menuFetcherV3 } from '@library/menu-service'
import type { Menu } from '@library/menu-service/queries/menuApiMenu/types'

import { homeGetStarted } from 'actions/home'
import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import routesConfig from 'config/routes'

import { useGetCtaProps } from './Cta/ctaHooks'
import { knownVariants, defaultVariant, seo } from './homeConfig'
import { useIsCwcHomepageEnabled } from './hooks/useIsCwcHomepageEnabled'
import { getHreflangLinks } from './hreflangLinks'

const formatRecipes = (response?: Menu): Recipe[] => {
  if (!response || !response.recipes) {
    return []
  }

  return Object.values(response.recipes).map((recipe) => fromAPIRecipe(recipe))
}

const getHelmetLink = (variant: string) => {
  const {
    client: { home },
  } = routesConfig
  const knownVariant = knownVariants.includes(variant) ? variant : defaultVariant
  const canonical =
    knownVariant !== 'default' ? [{ rel: 'canonical', href: generateHref(home) }] : []

  // hreflang lives here rather than in GoustoHelmet because this Helmet renders on the client as
  // well as the server — tags emitted only server-side are reconciled away on hydration, leaving
  // nothing for a crawler that executes JavaScript.
  return [...canonical, ...getHreflangLinks(generateHref(home))]
}

type HomePageProps = { location: { query: { variant: string } } }

export const HomePageContent = ({ location }: HomePageProps) => {
  const dispatch = useDispatch()

  const { data: menuResponse } = useSWR(
    {
      numPortions: 2,
    },
    menuFetcherV3,
  )

  const link = useMemo(() => getHelmetLink(location.query.variant), [location.query.variant])

  const { ctaText, ctaUri } = useGetCtaProps()

  const showCwcHomepage = useIsCwcHomepageEnabled()

  const handleCWCPromoAppliedCTAClick = (uri: string) => () => {
    datadogRum.addAction('cwc_start_onboarding')
    dispatch(homeGetStarted(uri))
  }

  const handleSignupCTAClick = () => {
    datadogRum.addAction('start_onboarding')
    dispatch(homeGetStarted(ctaUri))
  }

  const recipes = useMemo(() => formatRecipes(menuResponse), [menuResponse])

  return (
    <>
      <Helmet
        title={seo.title}
        meta={seo.meta}
        link={link}
        style={[
          {
            cssText: `
              #react-root {
                height: 100%;
              }
            `,
          },
        ]}
      />

      {showCwcHomepage ? (
        <CWCHomepage
          signupUrl={ctaUri}
          onSignupCTAClick={handleSignupCTAClick}
          menu={menuResponse}
          menuUrl={routesConfig.client.menu}
          onMenuCTAClick={handleCWCPromoAppliedCTAClick(routesConfig.client.menu)}
        />
      ) : (
        <Homepage signupCTA={ctaText} onSignupCTAClick={handleSignupCTAClick} recipes={recipes} />
      )}

      <RibbonTriggerContainer name="home-page" />
    </>
  )
}
